import React, { useState, useEffect, useCallback } from 'react';
import {
  DomainVerificationPanel,
  RedirectRulesList,
  type DomainItem,
  type RedirectRule,
  domainsApi,
} from '..';
import { editorApi, type SiteRecord } from '../../editor';
import { Globe, Plus, Sparkles, ArrowRight } from 'lucide-react';

const webbuilderApi = { ...editorApi, ...domainsApi };

export function DomainsPage(): React.JSX.Element {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [domains, setDomains] = useState<DomainItem[]>([]);
  const [rules, setRules] = useState<RedirectRule[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);

  // New Domain Modal
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [newHostname, setNewHostname] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  // New Redirect Modal
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [newSourcePath, setNewSourcePath] = useState('');
  const [newTargetUrl, setNewTargetUrl] = useState('');
  const [newStatusCode, setNewStatusCode] = useState(301);

  // Load sites
  useEffect(() => {
    (async () => {
      try {
        const data = await webbuilderApi.listSites();
        if (Array.isArray(data) && data.length > 0) {
          setSites(data);
          setActiveSiteId(data[0].id);
        }
      } catch {
        // Fallback
      }
    })();
  }, []);

  // Load domains & redirects when activeSiteId changes
  const loadDomainData = useCallback(async (siteId: string) => {
    if (!siteId) return;
    try {
      const [domList, redList] = await Promise.all([
        webbuilderApi.listDomains(siteId),
        webbuilderApi.listRedirects(siteId),
      ]);
      if (Array.isArray(domList)) {
        setDomains(
          domList.map((d) => ({
            id: d.id,
            hostname: d.hostname,
            isPrimary: d.is_primary,
            txtVerificationToken: d.txt_verification_token,
            tlsStatus: (d.tls_status === 'active' || d.tls_status === 'ready' ? 'ready' : 'pending') as 'ready' | 'pending' | 'error',
            dnsVerifiedAt: d.dns_verified_at || null,
          }))
        );
      }
      if (Array.isArray(redList)) {
        setRules(
          redList.map((r) => ({
            id: r.id,
            sourcePath: r.source_path,
            targetUrl: r.target_url,
            statusCode: r.status_code,
          }))
        );
      }
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    if (activeSiteId) {
      loadDomainData(activeSiteId);
    }
  }, [activeSiteId, loadDomainData]);

  const handleVerify = async (domainId: string) => {
    if (!activeSiteId) return;
    setIsVerifying(true);
    try {
      await webbuilderApi.verifyDomain(activeSiteId, domainId);
      await loadDomainData(activeSiteId);
    } catch (err) {
      console.error('Verification failed', err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCreateDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSiteId || !newHostname) return;
    try {
      await webbuilderApi.createDomain(activeSiteId, {
        hostname: newHostname.toLowerCase().trim(),
        is_primary: isPrimary,
      });
      setShowDomainModal(false);
      setNewHostname('');
      setIsPrimary(false);
      await loadDomainData(activeSiteId);
    } catch (err) {
      console.error('Failed to create domain', err);
    }
  };

  const handleCreateRedirect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSiteId || !newSourcePath || !newTargetUrl) return;
    try {
      await webbuilderApi.createRedirect(activeSiteId, {
        source_path: newSourcePath.startsWith('/') ? newSourcePath : `/${newSourcePath}`,
        target_url: newTargetUrl,
        status_code: newStatusCode,
      });
      setShowRedirectModal(false);
      setNewSourcePath('');
      setNewTargetUrl('');
      await loadDomainData(activeSiteId);
    } catch (err) {
      console.error('Failed to create redirect', err);
    }
  };

  const handleDeleteRedirect = async (redirectId: string) => {
    if (!activeSiteId) return;
    try {
      await webbuilderApi.deleteRedirect(activeSiteId, redirectId);
      await loadDomainData(activeSiteId);
    } catch (err) {
      console.error('Failed to delete redirect', err);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-8">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card border border-border p-4 rounded-xl">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site:</span>
          <select
            value={activeSiteId}
            onChange={(e) => setActiveSiteId(e.target.value)}
            className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.subdomain_slug})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDomainModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs font-medium transition-opacity min-h-[38px]"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Connect Custom Domain</span>
          </button>
          <button
            type="button"
            onClick={() => setShowRedirectModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-medium transition-colors min-h-[38px]"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add 301 Redirect</span>
          </button>
        </div>
      </div>

      <DomainVerificationPanel
        domains={domains}
        onTriggerVerify={handleVerify}
        isVerifying={isVerifying}
      />

      <RedirectRulesList rules={rules} onDelete={handleDeleteRedirect} />

      {/* New Domain Modal */}
      {showDomainModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Connect Custom Domain</h2>
            </div>
            <form onSubmit={handleCreateDomain} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Domain Hostname</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. academy.helios.edu or admissions.apexcoach.in"
                  value={newHostname}
                  onChange={(e) => setNewHostname(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="primaryCheckbox"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="primaryCheckbox" className="text-xs text-foreground font-medium cursor-pointer">
                  Set as Primary Domain
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowDomainModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Add Domain
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Redirect Modal */}
      {showRedirectModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Add URL Redirect Rule</h2>
            </div>
            <form onSubmit={handleCreateRedirect} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Source Path</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. /old-admissions-2025"
                  value={newSourcePath}
                  onChange={(e) => setNewSourcePath(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Target URL</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. /courses/admissions"
                  value={newTargetUrl}
                  onChange={(e) => setNewTargetUrl(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">HTTP Status Code</label>
                <select
                  value={newStatusCode}
                  onChange={(e) => setNewStatusCode(Number(e.target.value))}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                >
                  <option value={301}>301 - Permanent Redirect (SEO Recommended)</option>
                  <option value={302}>302 - Temporary Redirect</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowRedirectModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Create Redirect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'Domains, TLS & SEO' };
